import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function Login() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const { login, isLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!username.trim() || !password.trim()) {
      toast.error('Bitte füllen Sie alle Felder aus');
      return;
    }

    const result = await login(username, password);

    if (result.success) {
      toast.success('Erfolgreich angemeldet');
      // Nach Token-Ablauf-Redirect zurück zur Seite wo der User war.
      // Nur app-interne Pfade akzeptieren (kein Open-Redirect).
      const redirect = searchParams.get('redirect');
      const target = redirect && redirect.startsWith('/') && !redirect.startsWith('//') ? redirect : '/home';
      navigate(target);
    } else {
      toast.error(result.error || 'Anmeldung fehlgeschlagen');
    }
  };

  return (
    <div className="min-h-screen bg-primary flex flex-col">
      {/* Brand Stripe oben */}
      <div className="h-1.5 bg-gradient-brand" />
      
      {/* Content Container */}
      <div className="flex-1 flex flex-col items-center justify-center px-4 py-8">
        {/* Logo & Branding */}
        <div className="text-center mb-8 animate-fade-in">
          <div className="w-48 h-24 bg-white rounded-xl flex items-center justify-center mx-auto mb-6 shadow-xl p-3">
            <img 
              src="/logo.png" 
              alt="Ludwig Zimmermann GmbH" 
              className="w-full h-full object-contain"
            />
          </div>
          <h1 className="text-2xl font-bold text-primary-foreground tracking-tight">
            Monteur-App
          </h1>
          <p className="text-primary-foreground/60 mt-1 text-sm tracking-wide">
            Bäder · Wärme · Solar
          </p>
        </div>

        {/* Login Card */}
        <div className="w-full max-w-sm animate-slide-up">
          <div className="bg-card rounded-2xl shadow-2xl p-6 border border-border/50">
            <h2 className="text-lg font-semibold text-foreground mb-5">Anmelden</h2>
            
            <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">
              <div>
                <label htmlFor="username" className="text-sm font-medium text-muted-foreground mb-1.5 block">
                  E-Mail
                </label>
                <input
                  id="username"
                  type="email"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="w-full h-12 px-4 bg-muted/50 border border-border rounded-xl text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                  placeholder="Ihre E-Mail-Adresse"
                  autoComplete="off"
                  disabled={isLoading}
                />
              </div>
              
              <div>
                <label htmlFor="password" className="text-sm font-medium text-muted-foreground mb-1.5 block">
                  Passwort
                </label>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full h-12 px-4 pr-12 bg-muted/50 border border-border rounded-xl text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                    placeholder="Ihr Passwort"
                    autoComplete="off"
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-muted-foreground hover:text-foreground transition-colors"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              
              <button
                type="submit"
                className="w-full h-12 bg-primary text-primary-foreground font-semibold rounded-xl hover:bg-primary/90 active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 mt-6 shadow-lg shadow-primary/25"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Anmelden...</span>
                  </>
                ) : (
                  'Anmelden'
                )}
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="text-center py-4">
        <p className="text-xs text-primary-foreground/40">
          © {new Date().getFullYear()} Ludwig Zimmermann GmbH
        </p>
      </div>
    </div>
  );
}